import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Instagram, Facebook, Star, Award, Clock, Shield, ShoppingBag, Package } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const Footer = ({ onBookingClick }: { onBookingClick: () => void }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      return;
    }
    
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <footer className="bg-foreground text-background">
      {/* Newsletter CTA Section */}
      <div className="bg-primary text-primary-foreground py-12">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold mb-2">Join 2,000+ Michigan Homeowners</h3>
          <p className="text-primary-foreground/90 mb-6">Get weekly organizing tips, before/after transformations, and exclusive discounts</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Enter your email" 
              className="flex-1 px-4 py-2 rounded-md text-foreground"
            />
            <Button className="bg-background text-foreground hover:bg-background/90">
              Get Free Tips
            </Button>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            
            {/* Company Info */}
            <div className="lg:col-span-1">
              <h3 className="text-2xl font-serif font-semibold mb-4">
                Organize by Design
              </h3>
              <p className="text-background/80 mb-6 leading-relaxed">
                Michigan's premier professional organizing service. Transforming cluttered homes 
                into calm, functional spaces that work for your lifestyle.
              </p>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className="text-background/80">5-Star Rated</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Shield className="w-4 h-4 text-green-400" />
                  <span className="text-background/80">Insured & Bonded</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Award className="w-4 h-4 text-blue-400" />
                  <span className="text-background/80">Certified Organizers</span>
                </div>
              </div>

              <div className="flex space-x-4">
                <a href="https://instagram.com/organizebydesignmichigan" aria-label="Instagram" target="_blank" rel="noopener noreferrer" className="text-background/60 hover:text-background transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://facebook.com/organizebydesignmichigan" aria-label="Facebook" target="_blank" rel="noopener noreferrer" className="text-background/60 hover:text-background transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-semibold mb-6 text-primary">Organizing Services</h4>
              <ul className="space-y-3 text-background/80">
                <li><button onClick={() => navigate('/home-reset')} className="hover:text-background transition-colors text-left">Home Reset Program</button></li>
                <li><button onClick={() => navigate('/adhd-systems')} className="hover:text-background transition-colors text-left">ADHD-Friendly Systems</button></li>
                <li><button onClick={() => navigate('/real-estate-prep')} className="hover:text-background transition-colors text-left">Real Estate Staging</button></li>
                <li><button onClick={() => navigate('/same-day')} className="hover:text-background transition-colors text-left">Emergency Decluttering</button></li>
                <li><button onClick={() => navigate('/cleaning')} className="hover:text-background transition-colors text-left">Deep Cleaning Services</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-background transition-colors text-left">Downsizing & Moving</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-background transition-colors text-left">Virtual Organization</button></li>
              </ul>
            </div>

            {/* Resources & Shop */}
            <div>
              <h4 className="text-lg font-semibold mb-6 text-primary">Resources & Shop</h4>
              <ul className="space-y-3 text-background/80">
                <li><button onClick={() => navigate('/blog')} className="hover:text-background transition-colors text-left">Organization Tips Blog</button></li>
                <li><button onClick={() => navigate('/resources')} className="hover:text-background transition-colors text-left">Free Organizing Guides</button></li>
                <li className="flex items-center gap-2 text-background/60">
                  <ShoppingBag className="w-4 h-4" />
                  <span>Organization Products</span>
                  <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">Coming Soon</span>
                </li>
                <li className="flex items-center gap-2 text-background/60">
                  <Package className="w-4 h-4" />
                  <span>Starter Kits</span>
                  <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">Coming Soon</span>
                </li>
              </ul>
              
              <div className="mt-6 p-4 bg-background/10 rounded-lg">
                <p className="text-sm text-background/90 mb-2">💡 Quick Tip:</p>
                <p className="text-sm text-background/70">"Start with just 15 minutes a day. Small progress leads to big transformations!"</p>
              </div>
            </div>

            {/* Contact & Areas */}
            <div>
              <h4 className="text-lg font-semibold mb-6 text-primary">Contact & Service Areas</h4>
              <div className="space-y-4 mb-6">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                  <div>
                    <a href="tel:+16167270343" className="text-background hover:text-primary-foreground font-medium block">616.727.0343</a>
                    <span className="text-sm text-background/70">Call or Text</span>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                  <a href="mailto:hello@organizebydesignmichigan.com" className="text-background/80 hover:text-background underline underline-offset-2 break-all">hello@organizebydesignmichigan.com</a>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div className="text-background/80">
                    <p className="font-medium">Serving Southwest Michigan:</p>
                    <p className="text-sm">Buchanan • Niles • St. Joseph</p>
                    <p className="text-sm">Benton Harbor • Berrien Springs • Stevensville</p>
                    <p className="text-sm">Bridgman • New Buffalo • Dowagiac</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-primary" />
                  <div className="text-background/80 text-sm">
                    <p>Mon-Fri: 8am-6pm</p>
                    <p>Sat: 9am-4pm</p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={onBookingClick}
                className="w-full luxury-gradient text-primary-foreground font-medium mb-4"
              >
                Book Free Consultation
              </Button>
              
              <p className="text-xs text-background/60 text-center">Response within 2 hours • Same-day service available</p>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-background/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-background/60 text-sm">
                © 2024 Organize by Design Michigan LLC. All rights reserved.
              </p>
              <div className="flex flex-wrap gap-6 text-sm text-background/60">
                <button onClick={() => navigate('/privacy')} className="hover:text-background transition-colors">Privacy Policy</button>
                <button onClick={() => navigate('/terms')} className="hover:text-background transition-colors">Terms of Service</button>
                <a href="/sitemap.xml" className="hover:text-background transition-colors">Sitemap</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;